import { Price, Token } from '@uniswap/sdk-core'
import {
  nearestUsableTick,
  priceToClosestTick,
  TickMath,
  tickToPrice,
} from '@uniswap/v3-sdk'

import { active_chain } from '../../blockchain/config'
import type { symbol_to_decimal_type } from '../portfolio'
import type { FundSymbol } from '../products'

import { tokens_to_amount } from './block_helpers'

export function price_to_usable_tick({
  price,
  fund_symbol,
  fund_address,
  usdc_address,
  symbol_to_decimal,
  tickSpacing,
}: {
  price: number
  fund_symbol: FundSymbol
  fund_address: CryptoAddress
  usdc_address: CryptoAddress
  symbol_to_decimal: Extract<symbol_to_decimal_type, { isSuccess: true }>
  tickSpacing: number
}) {
  const fund_token = convert_to_u_token({
    symbol: fund_symbol,
    address: fund_address,
    decimals: symbol_to_decimal[fund_symbol],
  })

  const usdc_token = convert_to_u_token({
    symbol: 'USDC',
    address: usdc_address,
    decimals: symbol_to_decimal['USDC'],
  })

  const price_tick = nearestUsableTick(
    priceToClosestTick(
      new Price(
        fund_token,
        usdc_token,
        tokens_to_amount(1, symbol_to_decimal[fund_symbol]).toString(),
        tokens_to_amount(price, symbol_to_decimal['USDC']).toString(),
      ),
    ),
    tickSpacing,
  )

  return price_tick
}

export function next_price_tick({
  price_tick,
  direction,
  fund_symbol,
  fund_address,
  usdc_address,
  symbol_to_decimal,
  tickSpacing,
}: {
  price_tick: number
  direction: 'up' | 'down'
  fund_symbol: FundSymbol
  fund_address: CryptoAddress
  usdc_address: CryptoAddress
  symbol_to_decimal: Extract<symbol_to_decimal_type, { isSuccess: true }>
  tickSpacing: number
}) {
  const fund_token = convert_to_u_token({
    symbol: fund_symbol,
    address: fund_address,
    decimals: symbol_to_decimal[fund_symbol],
  })

  const usdc_token = convert_to_u_token({
    symbol: 'USDC',
    address: usdc_address,
    decimals: symbol_to_decimal['USDC'],
  })

  const price = Number(
    tickToPrice(fund_token, usdc_token, price_tick).toFixed(2),
  )

  let new_price = price
  let new_price_tick: number
  do {
    new_price = new_price + (direction == 'up' ? 0.01 : -0.01)
    new_price = Number(new_price.toFixed(2))

    new_price_tick = nearestUsableTick(
      priceToClosestTick(
        new Price(
          fund_token,
          usdc_token,
          tokens_to_amount(1, symbol_to_decimal[fund_symbol]).toString(),
          tokens_to_amount(new_price, symbol_to_decimal['USDC']).toString(),
        ),
      ),
      tickSpacing,
    )
  } while (price_tick == new_price_tick)

  return {
    new_price_tick,
    new_price,
  }
}

export function max_price_tick({
  fund_symbol,
  fund_address,
  usdc_address,
  symbol_to_decimal,
}: {
  fund_symbol: FundSymbol
  fund_address: CryptoAddress
  usdc_address: CryptoAddress
  symbol_to_decimal: Extract<symbol_to_decimal_type, { isSuccess: true }>
}): {
  tick: number
  max_price: number
} {
  const fund_token = convert_to_u_token({
    symbol: fund_symbol,
    address: fund_address,
    decimals: symbol_to_decimal[fund_symbol],
  })

  const usdc_token = convert_to_u_token({
    symbol: 'USDC',
    address: usdc_address,
    decimals: symbol_to_decimal['USDC'],
  })

  const low_tick_price: number = Number(
    tickToPrice(fund_token, usdc_token, TickMath.MIN_TICK).toFixed(2),
  )
  const high_tick_price: number = Number(
    tickToPrice(fund_token, usdc_token, TickMath.MAX_TICK).toFixed(2),
  )

  if (low_tick_price > high_tick_price) {
    return {
      tick: TickMath.MIN_TICK,
      max_price: low_tick_price,
    }
  }
  return {
    tick: TickMath.MAX_TICK,
    max_price: high_tick_price,
  }
}

export function convert_to_u_token(args: {
  symbol: string
  address: CryptoAddress
  decimals: number
}): Token {
  return new Token(
    active_chain.id,
    args.address,
    args.decimals,
    args.symbol,
    args.symbol,
  )
}
